import { useRef, useState } from "react";
import cx from "classnames";

import Angle from "common/components/Angle";
import Collapse from "common/components/Collapse";
import { toBrowserLocalISODate, toDate } from "common/utils/dates";
import { noop } from "common/utils/noop";

import Button from "client/common/components/buttons/Button";
import Calendar from "client/common/components/calendars/Calendar";
import ErrorMessage from "client/common/components/errors/ErrorMessage";
import BaseInput from "client/common/components/inputs/BaseInput";

import styles from "../../styles/CalendarAccordion.module.less";

type CalendarAccordionProps = {
  label: string;
  date: Date | null;
  onChange: (date: Date) => void;
  /** Renders a clear button beneath the calendar while a date is selected. */
  onClear?: () => void;
  clearLabel?: string;
  /** Days before this date are disabled. Also the month shown when no date is selected. */
  minDate?: Date;
  /** Shown beneath the floated label while empty. Without it, the label fills the empty field. */
  placeholder?: string;
  disabled?: boolean;
  errors?: string[];
  className?: string;
};

const CalendarAccordion = ({
  label,
  date,
  onChange,
  onClear,
  clearLabel = "Clear",
  minDate,
  placeholder,
  disabled = false,
  errors = [],
  className,
}: CalendarAccordionProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const displayValue = date ? toDate(toBrowserLocalISODate(date)) : "";

  if (disabled && isCalendarOpen) {
    setIsCalendarOpen(false);
  }

  const closeCalendarAndRestoreFocus = () => {
    setIsCalendarOpen(false);
    toggleRef.current?.focus();
  };

  return (
    <div className={className}>
      <div
        className={cx(
          styles.dateField,
          errors.length > 0 && styles.error,
          disabled && styles.disabled,
        )}
      >
        <BaseInput
          value={displayValue}
          onChange={noop}
          useLabelWrapper={false}
          styles={{ control: styles.control }}
          renderInputControl={() => (
            <button
              ref={toggleRef}
              type="button"
              className={styles.toggle}
              aria-expanded={isCalendarOpen}
              aria-label={displayValue ? `${label}: ${displayValue}` : label}
              disabled={disabled}
              onClick={() => setIsCalendarOpen((isOpen) => !isOpen)}
            />
          )}
          renderInputDisplay={({ className: displayClassName }) => (
            <>
              <BaseInput.AnimationContainer
                label={label}
                isActive={isCalendarOpen || (!displayValue && !!placeholder)}
              >
                <div className={cx(styles.dateValue, displayClassName)}>
                  {displayValue || placeholder}
                </div>
              </BaseInput.AnimationContainer>
              <Angle className={styles.dateIcon} direction="down" isActive={isCalendarOpen} />
            </>
          )}
        />
        <Collapse.UnmountClosed isOpened={isCalendarOpen}>
          <Calendar
            classNames={{ container: styles.calendar }}
            initialMonth={date ?? minDate}
            disabledDays={minDate && { before: minDate }}
            selectedDays={date ?? undefined}
            onSelect={(selectedDate) => {
              onChange(selectedDate);
              closeCalendarAndRestoreFocus();
            }}
          />
          {onClear && date && (
            <div className={styles.clearRow}>
              <Button
                variant="secondary"
                size="s"
                onClick={() => {
                  onClear();
                  closeCalendarAndRestoreFocus();
                }}
              >
                {clearLabel}
              </Button>
            </div>
          )}
        </Collapse.UnmountClosed>
      </div>
      {errors.map((error) => (
        <ErrorMessage key={error}>{error}</ErrorMessage>
      ))}
    </div>
  );
};

export default CalendarAccordion;
